/**
 * Knowledge Base Articles permissions from dynamic menu cache (hdDynamicMenuCache).
 * Single Articles screen ("My Articles") — can_view / can_insert / can_update / can_delete from get-menus.
 */

import { loadMenuDataForNavigation } from './notificationNavigation';

/** Articles screen keys after normalization: "My Articles" → my-articles. */
const ARTICLES_SCREEN_KEYS = new Set(['my-articles', 'article-management']);

/** Route suffixes (API may use /knowledge/my-articles or /agent/knowledge/my-articles). */
const ARTICLES_ROUTE_SUFFIXES = [
  '/knowledge/my-articles',
  '/agent/knowledge/my-articles',
  '/agent/knowledge/articles',
  '/my-articles',
];

/** Normalize screen_name / screen_label to comparable slug: "My Articles" → "my-articles". */
export function toScreenKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

function normalizeRoute(screen) {
  return String(screen?.routing || screen?.route || '')
    .replace(/\/+$/, '')
    .toLowerCase();
}

function screenKeysFor(screen) {
  const keys = new Set();
  const name = screen?.screenName || screen?.screen_name;
  const label = screen?.screenLable || screen?.screen_label || screen?.screenLabel;
  if (name) keys.add(toScreenKey(name));
  if (label) keys.add(toScreenKey(label));
  return keys;
}

function permissionsFromScreen(screen) {
  if (!screen) {
    return { canView: false, canInsert: false, canUpdate: false, canDelete: false };
  }
  return {
    canView: Boolean(screen.canView),
    canInsert: Boolean(screen.canInsert),
    canUpdate: Boolean(screen.canUpdate),
    canDelete: Boolean(screen.canDelete),
  };
}

function screenHasAnyAccess(screen) {
  return Boolean(
    screen?.canView || screen?.canUpdate || screen?.canInsert || screen?.canDelete
  );
}

function matchesScreenKeySet(screen, keySet) {
  const keys = screenKeysFor(screen);
  for (const k of keys) {
    if (keySet.has(k)) return true;
  }
  return false;
}

function matchesRouteSuffixes(screen, suffixes) {
  const route = normalizeRoute(screen);
  if (!route) return false;
  return suffixes.some((suffix) => {
    const s = suffix.toLowerCase().replace(/\/+$/, '');
    return route === s || route.endsWith(s);
  });
}

/**
 * Find the Articles screen grant from the cached dynamic menu.
 */
export function findKbMenuScreen(screenKeys, routeSuffixes = []) {
  const keys = screenKeys instanceof Set ? screenKeys : new Set(screenKeys || []);

  for (const module of loadMenuDataForNavigation()) {
    if (module?.isActive === false) continue;
    for (const screen of module.submenu || []) {
      if (!screen || screen.isActive === false || !screenHasAnyAccess(screen)) continue;
      const nameMatch = keys.size > 0 && matchesScreenKeySet(screen, keys);
      const routeMatch = routeSuffixes.length > 0 && matchesRouteSuffixes(screen, routeSuffixes);
      if (nameMatch || routeMatch) {
        return screen;
      }
    }
  }
  return null;
}

function findArticlesScreen() {
  return findKbMenuScreen(ARTICLES_SCREEN_KEYS, ARTICLES_ROUTE_SUFFIXES);
}

/** Articles screen permissions from get-menus (My Articles only). */
export function getArticlesScreenPermissions() {
  return permissionsFromScreen(findArticlesScreen());
}

/** View-only users see published/live articles; insert/update also unlocks drafts in list API. */
export function canIncludeKbDraftsInList() {
  const { canInsert, canUpdate } = getArticlesScreenPermissions();
  return canInsert || canUpdate;
}

/** Approve draft — Articles can_update (POST .../approve). */
export function canApproveKbArticle() {
  return getArticlesScreenPermissions().canUpdate;
}

/** Publish draft to live — Articles can_update (PUT status → live). */
export function canPublishKbArticle() {
  return getArticlesScreenPermissions().canUpdate;
}

/** Approve or publish — both require Articles can_update. */
export function canApproveOrPublishKbArticle() {
  return getArticlesScreenPermissions().canUpdate;
}

/** @deprecated Use getArticlesScreenPermissions */
export function getCurrentKbScreenPermissions() {
  return getArticlesScreenPermissions();
}
